// Bulk watermark removal for every listing in a contact's minisite (Haim: "add a way to
// remove watermarks for all listings (skip those that are done obviously). reflect status
// easily (how many listings have watermark)").
//
// REUSE, not reimplementation. The single-listing feature already owns all the hard
// parts and this file drives them:
//  - `removeListingWatermarks` (useWebotServer → POST /api/v1/removeListingWatermarks) —
//    the exact same call WatermarkRemovalStudio makes. It is listing-scoped and takes an
//    optional `imageUrls` subset, which is precisely the bulk primitive we need.
//  - `buildWatermarkFreeUrlMap` (via useMatchesWatermarkStatus) — the same definition of
//    "clean" the studio/chip/ListingEditDialog use.
// Nothing under components/_dashboard/watermarks/ was modified.
//
// HONESTY (the rule ListingEditDialog documents, applied at set level):
//  - "Clean" is per IMAGE, so a listing counts as clean only when EVERY one of its
//    images has a clean version. Never trust a listing-level claim.
//  - Listings with no marketplace doc / no images are not counted at all (nothing to
//    remove), rather than being silently counted as clean.
//  - Removal is a real AI job that takes time. The dialog says "בתהליך" per listing and
//    only flips to "הוסרו" on the server's own per-image `completed` verdict; a partial
//    result is reported as partial, never rounded up to success.
//
// COST/LOAD: `forceRegenerate` is deliberately never sent (it would REPLACE existing
// clean versions), only the still-dirty URLs are requested, and requests run at
// BULK_CONCURRENCY — this must not become an unbounded fan-out at the backend.
//
// MARKET vs OWN (customer report 2026-08-11: "we propose to remove watermarks from all
// listings, but that isn't really needed on listings that aren't from the שוק"): the
// checkbox list is SPLIT in two, and only the market section is ticked by default.
// A scraped yad2/madlan photo carries the publisher's watermark — that is what this
// feature exists for. The realtor's own (and their agency's) photos are already theirs,
// so removal there is offered but never assumed: their section renders unticked, under
// a line saying why. Selection state is therefore an OVERRIDE map over that per-group
// default, not a flat opt-out set — see `selectionOverrides`.
//
// SUBSET SELECTION (Haim: "I still want to be able to remove watermark on pictures from
// specific listings only"): the confirm step is a CHECKBOX list, not a take-it-or-leave-it
// batch. The run engine already accepted an arbitrary subset — `start(targets)` is what the
// retry path always used — so selection is pure dialog-local UI state and useBulkWatermarkRemoval
// is untouched. Rows deliberately have NO per-row "run this one now" shortcut (Haim reject
// round: it duplicated the single-listing entry points); one listing = tick only it and run.
// The per-listing entry point in the minisite edit dialog / card badge
// (ListingWatermarkPanel → WatermarkRemovalStudio) stays the way to work image-by-image;
// this dialog is the multi-listing surface.
//
// TERMS GATE: every run starts behind the shared WatermarkTermsCheckbox — the realtor
// confirms they hold the rights to remove watermarks and market these properties. First
// acceptance is persisted on the user doc (watermarkTermsAcceptedAt) as proof; the box is
// shown (pre-checked) before every later run. Same gate as WatermarkRemovalStudio.
import { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Chip,
  Alert,
  Stack,
  Button,
  Dialog,
  Divider,
  Checkbox,
  Typography,
  IconButton,
  DialogTitle,
  DialogActions,
  DialogContent,
  LinearProgress,
  CircularProgress
} from "@material-ui/core";
import {
  Close,
  Block,
  Warning,
  Schedule,
  CheckCircle,
  AutoFixHigh,
  ErrorOutline
} from "@material-ui/icons";
import useWebotServer from "hooks/useWebotServer";
import {
  WatermarkTermsCheckbox,
  useWatermarkTermsAccepted
} from "components/_dashboard/watermarks/WatermarkTermsCheckbox";
import type {
  ListingWatermarkStatus,
  WatermarkGroupSummary
} from "./useMatchesWatermarkStatus";

/**
 * How many listings are cleaned at once. Each request fans out server-side across ALL of
 * that listing's dirty images, so this is already N×images in flight — keep it small.
 */
const BULK_CONCURRENCY = 2;

export type BulkOutcome =
  | "pending" // queued, not started
  | "running" // request in flight (the AI job is actually running)
  | "done" // every requested image came back clean
  | "partial" // some images cleaned, some failed
  | "failed" // nothing cleaned
  | "cancelled"; // never started — the realtor stopped the run

export type BulkRunItem = {
  listingId: string;
  label: string;
  /** Images we asked the server to clean (the dirty ones only). */
  requestedCount: number;
  cleanedCount: number;
  failedCount: number;
  outcome: BulkOutcome;
  errorMessage?: string;
};

export type BulkRunState = {
  status: "idle" | "running" | "finished";
  items: BulkRunItem[];
  cancelRequested: boolean;
};

export type BulkRunTarget = Pick<ListingWatermarkStatus, "listingId" | "label" | "pendingUrls">;

export type BulkWatermarkRun = {
  state: BulkRunState;
  isRunning: boolean;
  /** Listings finished (in any terminal state) / total, for progress UI. */
  progress: { done: number; total: number };
  start: (targets: BulkRunTarget[]) => void;
  cancel: () => void;
  reset: () => void;
};

const TERMINAL: ReadonlySet<BulkOutcome> = new Set<BulkOutcome>([
  "done",
  "partial",
  "failed",
  "cancelled"
]);

const IDLE_STATE: BulkRunState = { status: "idle", items: [], cancelRequested: false };

/**
 * Owns the bulk run. Deliberately lives in the TAB, not in the dialog, so closing the
 * dialog (or switching sub-tab) never kills a run in progress — the realtor can walk away
 * and the header chip keeps reporting "בתהליך".
 */
export function useBulkWatermarkRemoval(): BulkWatermarkRun {
  const { removeListingWatermarks } = useWebotServer();
  const [state, setState] = useState<BulkRunState>(IDLE_STATE);
  const cancelRef = useRef(false);
  const runningRef = useRef(false);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // The requests keep going server-side after unmount (results land on the listing docs
  // regardless); we just stop touching React state.
  const safeSetState = (updater: (prev: BulkRunState) => BulkRunState): void => {
    if (mountedRef.current) setState(updater);
  };

  const start = (targets: BulkRunTarget[]): void => {
    if (runningRef.current || targets.length === 0) return;
    runningRef.current = true;
    cancelRef.current = false;

    safeSetState(() => ({
      status: "running",
      cancelRequested: false,
      items: targets.map((target) => ({
        listingId: target.listingId,
        label: target.label,
        requestedCount: target.pendingUrls.length,
        cleanedCount: 0,
        failedCount: 0,
        outcome: "pending" as BulkOutcome
      }))
    }));

    const patch = (listingId: string, changes: Partial<BulkRunItem>): void =>
      safeSetState((prev) => ({
        ...prev,
        items: prev.items.map((item) =>
          item.listingId === listingId ? { ...item, ...changes } : item
        )
      }));

    const runOne = async (target: BulkRunTarget): Promise<void> => {
      patch(target.listingId, { outcome: "running" });
      try {
        const response = await removeListingWatermarks({
          listingId: target.listingId,
          imageUrls: target.pendingUrls
          // No forceRegenerate: a bulk pass must never REPLACE existing clean versions.
        });
        const results = response?.results ?? [];
        const cleaned = results.filter((result) => result.status === "completed").length;
        const failed = results.filter((result) => result.status === "failed");
        const succeeded = Boolean(response?.success);
        patch(target.listingId, {
          cleanedCount: cleaned,
          failedCount: failed.length,
          outcome: succeeded ? "done" : cleaned > 0 ? "partial" : "failed",
          errorMessage: succeeded
            ? undefined
            : response?.errorMessage ?? failed.find((f) => f.errorMessage)?.errorMessage
        });
      } catch (err) {
        console.error(
          `BulkWatermarkRemoval: removeListingWatermarks failed for ${target.listingId}`,
          err
        );
        patch(target.listingId, {
          failedCount: target.pendingUrls.length,
          outcome: "failed",
          errorMessage: "הבקשה נכשלה"
        });
      }
    };

    // Bounded worker pool over a shared queue. `shift()` is safe — JS is single-threaded,
    // so two workers can never take the same target.
    const queue = [...targets];
    const worker = async (): Promise<void> => {
      for (;;) {
        const target = queue.shift();
        if (!target) return;
        if (cancelRef.current) {
          patch(target.listingId, { outcome: "cancelled" });
          continue; // drain the queue so every row gets a final state
        }
        await runOne(target);
      }
    };

    const workerCount = Math.min(BULK_CONCURRENCY, targets.length);
    void Promise.all(Array.from({ length: workerCount }, () => worker())).finally(() => {
      runningRef.current = false;
      safeSetState((prev) => ({ ...prev, status: "finished" }));
    });
  };

  const cancel = (): void => {
    // Stops issuing NEW requests. In-flight AI jobs can't be recalled (the transport has
    // no abort signal), so the UI promises exactly that and nothing more.
    cancelRef.current = true;
    safeSetState((prev) => ({ ...prev, cancelRequested: true }));
  };

  const reset = (): void => {
    if (runningRef.current) return;
    safeSetState(() => IDLE_STATE);
  };

  const done = state.items.filter((item) => TERMINAL.has(item.outcome)).length;

  return {
    state,
    isRunning: state.status === "running",
    progress: { done, total: state.items.length },
    start,
    cancel,
    reset
  };
}

// ── header status ───────────────────────────────────────────────────────────────

type StatusChipProps = {
  /** yad2 / madlan listings — the only ones the status LINE is about. */
  market: WatermarkGroupSummary;
  /** Own/agency listings still carrying un-cleaned images. Only decides whether the
   *  way into the dialog is offered — never counted in the status line. */
  ownPendingCount: number;
  isRunning: boolean;
  progress: { done: number; total: number };
  onManage: () => void;
};

/**
 * The at-a-glance answer to "how many MARKET listings still have watermarks", plus the
 * way in. Renders nothing when there is neither a market status to report nor anything
 * left to remove.
 *
 * The count is deliberately market-only. Counting the realtor's own listings made a
 * minisite built from their own portfolio report "0 מתוך 5 נכסים נקיים" and push a
 * removal nobody needed — the report this split came from. Their listings stay
 * reachable: the button below opens the dialog, where they have their own (unticked)
 * section, and every card keeps its per-listing affordance.
 */
export function MatchesWatermarkStatusChip({
  market,
  ownPendingCount,
  isRunning,
  progress,
  onManage
}: StatusChipProps): JSX.Element | null {
  const hasSomethingToRemove = market.pendingStatuses.length > 0 || ownPendingCount > 0;
  if (!isRunning && market.trackedCount === 0 && !hasSomethingToRemove) return null;

  // No market listings ⇒ no watermark story to tell, so no chip — just the quiet way in.
  const showChip = isRunning || market.trackedCount > 0;

  return (
    <Stack direction="row" alignItems="center" gap={0.75} flexWrap="wrap">
      {showChip && (
        <Chip
          size="small"
          color={
            isRunning
              ? "warning"
              : market.allClean
              ? "success"
              : market.cleanCount
              ? "info"
              : "default"
          }
          icon={
            isRunning ? (
              <CircularProgress size={12} sx={{ ml: 1 }} color="inherit" />
            ) : market.allClean ? (
              <CheckCircle />
            ) : undefined
          }
          label={
            isRunning
              ? `מסיר סימני מים… ${progress.done} מתוך ${progress.total} נכסים`
              : market.allClean
              ? `סימני מים: כל ${market.trackedCount} נכסי השוק נקיים`
              : `סימני מים: ${market.cleanCount} מתוך ${market.trackedCount} נכסי השוק נקיים`
          }
        />
      )}
      {hasSomethingToRemove && (
        <Button
          size="small"
          variant="outlined"
          startIcon={<AutoFixHigh />}
          onClick={onManage}
          disabled={isRunning}
        >
          {/* Wording standardized to the feature's name (Haim reject round) — the dialog
              behind it still lets the realtor pick which listings run. */}
          הסרת סימני מים
        </Button>
      )}
    </Stack>
  );
}

// ── the bulk dialog ─────────────────────────────────────────────────────────────

const OUTCOME_LABELS: Record<BulkOutcome, string> = {
  pending: "ממתין",
  running: "בתהליך",
  done: "הוסרו",
  partial: "הוסרו חלקית",
  failed: "נכשל",
  cancelled: "בוטל"
};

function OutcomeIcon({ outcome }: { outcome: BulkOutcome }): JSX.Element {
  if (outcome === "running") return <CircularProgress size={16} />;
  if (outcome === "done") return <CheckCircle fontSize="small" color="success" />;
  if (outcome === "partial") return <Warning fontSize="small" color="warning" />;
  if (outcome === "failed") return <ErrorOutline fontSize="small" color="error" />;
  if (outcome === "cancelled") return <Block fontSize="small" color="disabled" />;
  return <Schedule fontSize="small" color="disabled" />;
}

function RunRow({ item }: { item: BulkRunItem }): JSX.Element {
  const detail =
    item.outcome === "done"
      ? `${item.cleanedCount} תמונות`
      : item.outcome === "partial"
      ? `${item.cleanedCount} מתוך ${item.requestedCount} תמונות · ${item.errorMessage ?? "חלק נכשלו"}`
      : item.outcome === "failed"
      ? item.errorMessage ?? "לא הוסרו סימני מים"
      : `${item.requestedCount} תמונות`;

  return (
    <Stack direction="row" alignItems="center" gap={1} sx={{ py: 0.75 }}>
      <Box sx={{ width: 20, display: "flex", justifyContent: "center", flexShrink: 0 }}>
        <OutcomeIcon outcome={item.outcome} />
      </Box>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography variant="body2" noWrap title={item.label}>
          {item.label}
        </Typography>
        <Typography variant="caption" color="text.secondary" noWrap title={detail}>
          {detail}
        </Typography>
      </Box>
      <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
        {OUTCOME_LABELS[item.outcome]}
      </Typography>
    </Stack>
  );
}

/**
 * One checkbox group. Two of these render — market (ticked by default) and own
 * (unticked) — so the realtor can see at a glance which listings the run is really
 * about, and tick into the other group deliberately rather than by omission.
 */
function SelectionSection({
  title,
  note,
  statuses,
  isSelected,
  onToggleListing,
  onToggleAll
}: {
  title: string;
  note: string;
  statuses: ListingWatermarkStatus[];
  isSelected: (status: ListingWatermarkStatus) => boolean;
  onToggleListing: (status: ListingWatermarkStatus) => void;
  onToggleAll: (statuses: ListingWatermarkStatus[], select: boolean) => void;
}): JSX.Element | null {
  if (statuses.length === 0) return null;

  const selectedCount = statuses.filter(isSelected).length;
  const allSelected = selectedCount === statuses.length;

  return (
    <Box sx={{ mb: 1.5 }}>
      <Stack direction="row" alignItems="center" gap={1}>
        <Checkbox
          size="small"
          sx={{ p: 0.5, flexShrink: 0 }}
          checked={allSelected}
          indeterminate={selectedCount > 0 && !allSelected}
          onChange={() => onToggleAll(statuses, !allSelected)}
        />
        <Typography variant="subtitle2">{`${title} (${selectedCount}/${statuses.length})`}</Typography>
      </Stack>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: "block", pb: 0.5, pl: 4.5 }}
      >
        {note}
      </Typography>
      {statuses.map((status) => (
        <Stack
          key={status.listingId}
          direction="row"
          alignItems="center"
          gap={1}
          sx={{ py: 0.5, pl: 2 }}
        >
          <Checkbox
            size="small"
            sx={{ p: 0.5, flexShrink: 0 }}
            checked={isSelected(status)}
            onChange={() => onToggleListing(status)}
          />
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography variant="body2" noWrap title={status.label}>
              {status.label}
            </Typography>
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
            {`${status.cleanedCount} מתוך ${status.totalCount} תמונות נקיות`}
          </Typography>
        </Stack>
      ))}
    </Box>
  );
}

type BulkDialogProps = {
  open: boolean;
  onClose: () => void;
  /** Live market summary (yad2 / madlan) — pre-selected for the run. */
  market: WatermarkGroupSummary;
  /** Live own/agency summary — listed, explained, and NOT pre-selected. */
  own: WatermarkGroupSummary;
  run: BulkWatermarkRun;
};

export function BulkWatermarkRemovalDialog({
  open,
  onClose,
  market,
  own,
  run
}: BulkDialogProps): JSX.Element {
  const { state, isRunning, progress, start, cancel, reset } = run;

  // Rights acknowledgment — the run cannot start until checked. Pre-checked once the
  // user has ever accepted (persisted on the user doc); same shared gate as the
  // single-listing studio.
  const terms = useWatermarkTermsAccepted();

  // Selection = per-group DEFAULT (market on, own off) plus the realtor's explicit
  // overrides. Deliberately not a flat opt-out set: the pending lists are live (they
  // shrink as clean versions land, and grow when a listing is added to the minisite),
  // and only a default-plus-override model gives a listing that arrives mid-dialog the
  // right initial state — a new market listing arrives ticked, a new own listing
  // doesn't. Cleared every time the dialog opens.
  const [selectionOverrides, setSelectionOverrides] = useState<Record<string, boolean>>({});
  useEffect(() => {
    if (open) setSelectionOverrides({});
  }, [open]);

  const isSelected = (status: ListingWatermarkStatus): boolean =>
    selectionOverrides[status.listingId] ?? status.isMarketListing;

  const pendingStatuses = useMemo(
    () => [...market.pendingStatuses, ...own.pendingStatuses],
    [market.pendingStatuses, own.pendingStatuses]
  );
  const selectedStatuses = pendingStatuses.filter(isSelected);
  const selectedImageCount = selectedStatuses.reduce(
    (total, status) => total + status.pendingUrls.length,
    0
  );

  // Flips off `prev`, not off the render's `isSelected` — two toggles in one tick must
  // not both read the pre-batch value and cancel each other out.
  const toggleListing = (status: ListingWatermarkStatus): void =>
    setSelectionOverrides((prev) => ({
      ...prev,
      [status.listingId]: !(prev[status.listingId] ?? status.isMarketListing)
    }));

  const toggleAll = (statuses: ListingWatermarkStatus[], select: boolean): void =>
    setSelectionOverrides((prev) => ({
      ...prev,
      ...Object.fromEntries(statuses.map((status) => [status.listingId, select]))
    }));

  const failedIds = useMemo(
    () =>
      new Set(
        state.items
          .filter((item) => item.outcome === "failed" || item.outcome === "partial")
          .map((item) => item.listingId)
      ),
    [state.items]
  );

  // Retry re-reads the LIVE pending set, so images that did land are never re-requested.
  const retryTargets = pendingStatuses.filter((status) => failedIds.has(status.listingId));

  const handleClose = (): void => {
    // Finished runs are cleared on close so reopening starts from a clean confirm step;
    // a run in progress is left alone (closing is explicitly allowed).
    if (!isRunning) reset();
    onClose();
  };

  const percent = progress.total ? Math.round((progress.done / progress.total) * 100) : 0;
  const summary = {
    done: state.items.filter((item) => item.outcome === "done").length,
    partial: state.items.filter((item) => item.outcome === "partial").length,
    failed: state.items.filter((item) => item.outcome === "failed").length,
    cancelled: state.items.filter((item) => item.outcome === "cancelled").length
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ py: 2 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Stack>
            <Typography variant="h6">הסרת סימני מים — בחירת נכסים</Typography>
            <Typography variant="caption" color="text.secondary">
              {market.trackedCount > 0
                ? `${market.cleanCount} מתוך ${market.trackedCount} נכסי שוק כבר נקיים${
                    market.pendingImageCount
                      ? ` · ${market.pendingImageCount} תמונות ממתינות`
                      : ""
                  }`
                : own.trackedCount > 0
                ? "אין נכסים מהשוק במיניסייט — רק נכסים שלכם"
                : "אין נכסים עם תמונות להסרה"}
            </Typography>
          </Stack>
          <IconButton onClick={handleClose}>
            <Close />
          </IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent dividers>
        {state.status === "idle" ? (
          <Stack spacing={2} sx={{ pt: 1 }}>
            {pendingStatuses.length === 0 ? (
              <Alert severity="success">
                כל הנכסים במיניסייט של הלקוח כבר נקיים. סימני מים מסירים פעם אחת לנכס, וזה חל על כל הלקוחות.
              </Alert>
            ) : (
              <>
                {/* "Nothing selected" is a WARNING only when there was something the
                    dialog would normally have ticked. With no market listings pending,
                    an empty selection is the correct resting state — say so plainly
                    instead of nagging the realtor about their own photos. */}
                <Alert
                  severity={
                    selectedStatuses.length || !market.pendingStatuses.length
                      ? "info"
                      : "warning"
                  }
                >
                  {selectedStatuses.length
                    ? `ההסרה תרוץ על ${selectedStatuses.length} נכסים שנבחרו (${selectedImageCount} תמונות). תמונות שכבר נקיות מדולגות.`
                    : market.pendingStatuses.length
                    ? `לא נבחר אף נכס. סמנו את הנכסים שמהם להסיר סימני מים (${pendingStatuses.length} ממתינים).`
                    : "אין נכסים מהשוק שממתינים להסרה. אם בכל זאת רוצים להסיר סימני מים מנכס שלכם — סמנו אותו ברשימה."}
                </Alert>
                <Typography variant="body2" color="text.secondary">
                  ההסרה מתבצעת ב-AI ולוקחת זמן — אפשר לסגור את החלון וההסרה תמשיך ברקע.
                  הסרת סימני מים היא פעולה חד-פעמית לכל נכס — התמונות הנקיות יוצגו
                  לכל הלקוחות שלכם, גם בקישורים ששלחתם כבר. להסרה תמונה-תמונה: פתחו
                  את הנכס בעריכה.
                </Typography>
                <Divider />
                <Typography variant="body2">
                  {`נבחרו ${selectedStatuses.length} מתוך ${pendingStatuses.length} נכסים`}
                </Typography>
                {/* Rows are pick-only — no per-row "run this one now" icon (Haim reject
                    round: it started a run mid-selection and duplicated the card /
                    edit-dialog single-listing entry points). One listing = tick only it.
                    The market group comes FIRST and pre-ticked; the realtor's own
                    listings follow, unticked, with the reason spelled out — a realtor
                    must never be nudged into paying for removal on their own photos. */}
                <Box sx={{ maxHeight: 300, overflowY: "auto" }}>
                  <SelectionSection
                    title="נכסים מהשוק"
                    note="יד2 / מדלן — התמונות מגיעות עם סימן המים של המפרסם. מסומנים כברירת מחדל."
                    statuses={market.pendingStatuses}
                    isSelected={isSelected}
                    onToggleListing={toggleListing}
                    onToggleAll={toggleAll}
                  />
                  <SelectionSection
                    title="הנכסים שלי ושל המשרד"
                    note="אלה התמונות שלכם — לרוב אין בהן סימני מים להסרה, ולכן הן לא מסומנות. אפשר לסמן בכל זאת."
                    statuses={own.pendingStatuses}
                    isSelected={isSelected}
                    onToggleListing={toggleListing}
                    onToggleAll={toggleAll}
                  />
                </Box>
                <Divider />
                {/* Shown before EVERY run; pre-checked once accepted. First acceptance
                    is persisted on the user doc as proof of the watermark terms. */}
                <WatermarkTermsCheckbox {...terms} />
              </>
            )}
          </Stack>
        ) : (
          <Stack spacing={2} sx={{ pt: 1 }}>
            {isRunning ? (
              <>
                <Box>
                  <LinearProgress variant="determinate" value={percent} />
                </Box>
                <Typography variant="body2" color="text.secondary">
                  {state.cancelRequested
                    ? "עוצר — נכסים שכבר התחילו יסתיימו."
                    : `הסתיימו ${progress.done} מתוך ${progress.total} נכסים. אפשר לסגור את החלון — ההסרה תמשיך ברקע.`}
                </Typography>
              </>
            ) : (
              <Alert
                severity={summary.failed || summary.partial ? "warning" : "success"}
              >
                {`הסתיים: ${summary.done} נכסים הושלמו${
                  summary.partial ? ` · ${summary.partial} חלקית` : ""
                }${summary.failed ? ` · ${summary.failed} נכשלו` : ""}${
                  summary.cancelled ? ` · ${summary.cancelled} בוטלו` : ""
                }`}
              </Alert>
            )}
            <Divider />
            <Box sx={{ maxHeight: 300, overflowY: "auto" }}>
              {state.items.map((item) => (
                <RunRow key={item.listingId} item={item} />
              ))}
            </Box>
          </Stack>
        )}
      </DialogContent>

      <DialogActions>
        {state.status === "idle" && (
          <>
            <Button color="inherit" onClick={handleClose}>
              ביטול
            </Button>
            <Button
              variant="contained"
              startIcon={<AutoFixHigh />}
              disabled={selectedStatuses.length === 0 || !terms.accepted}
              onClick={() => start(selectedStatuses)}
            >
              {`הסרה מ-${selectedStatuses.length} נכסים שנבחרו`}
            </Button>
          </>
        )}
        {isRunning && (
          <>
            <Button color="inherit" onClick={cancel} disabled={state.cancelRequested}>
              עצירה
            </Button>
            <Button variant="contained" onClick={onClose}>
              סגירה (ממשיך ברקע)
            </Button>
          </>
        )}
        {state.status === "finished" && (
          <>
            {retryTargets.length > 0 && (
              <Button color="inherit" onClick={() => start(retryTargets)}>
                {`נסה שוב (${retryTargets.length})`}
              </Button>
            )}
            <Button variant="contained" onClick={handleClose}>
              סגירה
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}
